import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, Tabs, Spin, Alert, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@apollo/client';
import { subscribeToMessages } from 'graph/queries';
import { useTriggerSituationAnalysis } from '../../services/situationAnalysisService';
import { StakeholderMetaPills } from '../Utils/StakeholderMetaPills';
import './SituationAnalysisModal.sass';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;

interface SituationAnalysisModalProps {
    visible: boolean;
    onClose: () => void;
    onComplete: (projectId: string) => void;
    projectId?: string;
    projectName?: string;
}

interface AnalysisData {
    analysis?: string;
    summary?: string;
    stakes?: any[];
    risks?: any[];
    openings?: any[];
    stakeholders?: any[];
}

export const SituationAnalysisModal: React.FC<SituationAnalysisModalProps> = ({
    visible,
    onClose,
    onComplete,
    projectId,
    projectName
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [analysisData, setAnalysisData] = useState<AnalysisData>({});
    const [currentTab, setCurrentTab] = useState('analysis');
    const [error, setError] = useState<string | null>(null);
    const { triggerSituationAnalysis } = useTriggerSituationAnalysis();

    // Subscription pour recevoir les messages d'analyse en temps réel
    const { data: subscriptionData } = useSubscription(subscribeToMessages, {
        variables: { projectId: projectId || '' },
        skip: !projectId || !visible
    });

    // Traitement des messages reçus
    useEffect(() => {
        if (subscriptionData?.onProjectMessage) {
            const message = JSON.parse(subscriptionData.onProjectMessage.message);
            handleAnalysisMessage(message);
        }
    }, [subscriptionData]);

    const handleAnalysisMessage = (message: any) => {
        if (message.type === 'situation_analysis') {
            const { step, content } = message;

            setAnalysisData(prev => {
                const newData = { ...prev };

                switch (step) {
                    case 'analysis':
                        newData.analysis = content;
                        break;
                    case 'summary':
                        newData.summary = content;
                        break;
                    case 'stakes':
                        newData.stakes = Array.isArray(content) ? content : [content];
                        break;
                    case 'risks':
                        newData.risks = Array.isArray(content) ? content : [content];
                        break;
                    case 'openings':
                        newData.openings = Array.isArray(content) ? content : [content];
                        break;
                    case 'stakeholders':
                        newData.stakeholders = Array.isArray(content) ? content : [content];
                        break;
                }

                return newData;
            });

            // Changer d'onglet automatiquement selon le type de contenu reçu
            if (step && step !== 'analysis') {
                setCurrentTab(step);
            }
        } else if (message.type === 'analysis_error') {
            setError(message.error);
            setLoading(false);
        }
    };

    const handleSubmit = async (values: any) => {
        if (!projectId) {
            setError('ID de projet manquant');
            return;
        }

        setLoading(true);
        setError(null);
        setAnalysisData({});

        try {
            // Appel au service d'analyse de situation
            await triggerSituationAnalysis(
                values.organization,
                values.subject || projectName || '',
                projectId
            );

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur inconnue');
            setLoading(false);
        }
    };

    const handleComplete = () => {
        if (projectId) {
            onComplete(projectId);
        }
        onClose();
    };

    const renderAnalysisContent = () => {
        if (analysisData.analysis) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.context_title')}</Title>
                    <Paragraph>{analysisData.analysis}</Paragraph>
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_analysis')}</div>;
    };

    const renderSummaryContent = () => {
        if (analysisData.summary) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.summary_title')}</Title>
                    <Paragraph>{analysisData.summary}</Paragraph>
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_summary')}</div>;
    };

    const renderStakesContent = () => {
        if (analysisData.stakes && analysisData.stakes.length > 0) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.stakes_title')}</Title>
                    {analysisData.stakes.map((stake, index) => (
                        <div key={index} className="analysis-item">
                            <div className="item-title">{stake.name || `Enjeu ${index + 1}`}</div>
                            {stake.description && <div className="item-description">{stake.description}</div>}
                            {stake.trend && <div className="item-meta">{t('messages.situation_analysis.status')}: {stake.trend}</div>}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_stakes')}</div>;
    };

    const renderRisksContent = () => {
        if (analysisData.risks && analysisData.risks.length > 0) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.risks_title')}</Title>
                    {analysisData.risks.map((risk, index) => (
                        <div key={index} className="analysis-item">
                            <div className="item-title">{risk.name || `Risque ${index + 1}`}</div>
                            {risk.description && <div className="item-description">{risk.description}</div>}
                            {risk.likelihood && risk.impact && (
                                <div className="item-meta">{t('messages.situation_analysis.likelihood')}: {risk.likelihood}, {t('messages.situation_analysis.impact')}: {risk.impact}</div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_risks')}</div>;
    };

    const renderOpeningsContent = () => {
        if (analysisData.openings && analysisData.openings.length > 0) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.openings_title')}</Title>
                    {analysisData.openings.map((opening, index) => (
                        <div key={index} className="analysis-item">
                            <div className="item-title">{opening.name || `Ouverture ${index + 1}`}</div>
                            {opening.description && <div className="item-description">{opening.description}</div>}
                            {opening.likelihood && opening.impact && (
                                <div className="item-meta">{t('messages.situation_analysis.likelihood')}: {opening.likelihood}, {t('messages.situation_analysis.impact')}: {opening.impact}</div>
                            )}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_openings')}</div>;
    };

    const renderStakeholdersContent = () => {
        if (analysisData.stakeholders && analysisData.stakeholders.length > 0) {
            return (
                <div>
                    <Title level={4}>{t('messages.situation_analysis.stakeholders_title')}</Title>
                    {analysisData.stakeholders.map((stakeholder, index) => (
                        <div key={index} className="analysis-item">
                            <div className="item-title">{stakeholder.name || `Partie prenante ${index + 1}`}</div>
                            {stakeholder.description && <div className="item-description">{stakeholder.description}</div>}
                            <StakeholderMetaPills item={stakeholder} t={t} className="stakeholder-meta-pills analysis-item-stakeholder-meta" />
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="waiting-message">{t('messages.situation_analysis.waiting_stakeholders')}</div>;
    };

    const tabItems = [
        {
            key: 'analysis',
            label: t('messages.situation_analysis.context_title'),
            children: renderAnalysisContent()
        },
        {
            key: 'summary',
            label: t('messages.situation_analysis.summary_title'),
            children: renderSummaryContent()
        },
        {
            key: 'stakes',
            label: t('messages.situation_analysis.stakes_title'),
            children: renderStakesContent()
        },
        {
            key: 'risks',
            label: t('messages.situation_analysis.risks_title'),
            children: renderRisksContent()
        },
        {
            key: 'openings',
            label: t('messages.situation_analysis.openings_title'),
            children: renderOpeningsContent()
        },
        {
            key: 'stakeholders',
            label: t('messages.situation_analysis.stakeholders_title'),
            children: renderStakeholdersContent()
        }
    ];

    return (
        <Modal
            title={t('messages.situation_analysis.title')}
            open={visible}
            onCancel={onClose}
            width="80%"
            style={{ maxWidth: '1200px' }}
            className="SituationAnalysisModal"
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('actions.close')}
                </Button>,
                <Button
                    key="complete"
                    type="primary"
                    onClick={handleComplete}
                    disabled={!analysisData.summary}
                >
                    {t('messages.situation_analysis.complete_analysis')}
                </Button>
            ]}
        >
            {error && (
                <Alert
                    message="Erreur"
                    description={error}
                    type="error"
                    showIcon
                    className="error-alert"
                />
            )}

            {!loading && !analysisData.analysis && (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="form-container"
                >
                    <Form.Item
                        name="organization"
                        label={t('messages.situation_analysis.organization_label')}
                        rules={[{ required: true, message: t('messages.form.required_error') }]}
                    >
                        <Input placeholder={t('messages.situation_analysis.organization_placeholder')} />
                    </Form.Item>

                    <Form.Item
                        name="subject"
                        label={t('messages.situation_analysis.subject_label')}
                        initialValue={projectName}
                        rules={[{ required: true, message: t('messages.form.required_error') }]}
                    >
                        <Input placeholder={t('messages.situation_analysis.subject_placeholder')} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            {t('messages.situation_analysis.launch_analysis')}
                        </Button>
                    </Form.Item>
                </Form>
            )}

            {loading && (
                <div className="loading-container">
                    <Spin size="large" />
                    <div>{t('messages.situation_analysis.analysis_in_progress')}</div>
                </div>
            )}

            {analysisData.analysis && (
                <div className="analysis-content">
                    <Tabs
                        activeKey={currentTab}
                        onChange={setCurrentTab}
                        items={tabItems}
                    />
                </div>
            )}
        </Modal>
    );
};

export default SituationAnalysisModal;
